import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function HeroSection() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center pt-16">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black/25 via-black/10 to-black/25 z-10"></div>
        <img
          src="https://readdy.ai/api/search-image?query=Dark%20moody%20marble%20fluid%20art%20background%20with%20swirling%20organic%20patterns%20in%20deep%20forest%20green%20espresso%20brown%20and%20warm%20amber%20gold%20veins%2C%20luxurious%20abstract%20texture%20with%20dramatic%20flowing%20shapes%2C%20rich%20saturated%20color%20depth%2C%20editorial%20luxury%20aesthetic%20no%20text%20no%20objects%2C%20premium%20fashion%20website%20hero%20background&width=1600&height=1000&seq=hero-bg-dark-v3&orientation=landscape"
          alt=""
          className="absolute inset-0 w-full h-full object-cover object-top"
        />
      </div>

      <div className="relative z-20 w-full px-4 md:px-6 py-16 md:py-20">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-16 max-w-7xl mx-auto">

          <div className="flex-1 w-full text-center lg:text-left">
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-white leading-tight drop-shadow-md">
              The Ultimate<br />
              <span className="text-primary-300">PandabuySpreadsheet</span><br />
              Hub 2026
            </h1>
            <p className="mt-5 text-base md:text-lg text-white/80 leading-relaxed max-w-lg mx-auto lg:mx-0">
              Discover the most updated PandabuySpreadsheet collections with curated fashion, shoes, accessories, and viral TikTok finds — all in one place.
            </p>

            <div className="mt-8 flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-primary-500 text-white px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Pandabuy Sheet
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-right-up-line"></i>
                </span>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center gap-2 bg-white text-foreground-950 px-7 py-3.5 rounded-full text-sm font-semibold hover:bg-white/90 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-shopping-bag-line"></i>
                </span>
              </a>
            </div>

            <div className="mt-6 flex justify-center lg:justify-start" ref={dropdownRef}>
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full border-2 border-white/30 bg-white/10 backdrop-blur-sm text-white text-sm font-medium hover:border-white/50 hover:bg-white/20 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Browse Categories
                  <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                    <i className="ri-arrow-down-s-line"></i>
                  </span>
                </button>

                {dropdownOpen && (
                  <div className="absolute top-full mt-2 left-0 bg-background-50 rounded-xl border border-background-200 shadow-lg py-2 w-64 z-30 max-h-80 overflow-y-auto">
                    {categories.map((cat) => (
                      <a
                        key={cat.seq}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        onClick={() => setDropdownOpen(false)}
                        className="block px-5 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap cursor-pointer"
                      >
                        {cat.name}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 w-full flex items-center gap-4">
            <div className="flex-1 rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10" style={{ height: '420px' }}>
              <img
                src="https://readdy.ai/api/search-image?query=Elegant%20curated%20fashion%20flat%20lay%20featuring%20designer%20sneakers%20classic%20watch%20and%20leather%20accessories%20on%20soft%20dusty%20rose%20linen%20backdrop%2C%20minimalist%20editorial%20product%20photography%20with%20natural%20morning%20window%20lighting%2C%20subtle%20warm%20rose%20and%20taupe%20tones%2C%20high%20end%20lifestyle%20aesthetic%2C%20clean%20composition%20with%20generous%20negative%20space&width=600&height=840&seq=hero-main-v2&orientation=portrait"
                alt="Curated PandabuySpreadsheet fashion finds"
                className="w-full h-full object-cover object-top"
                title="PandabuySpreadsheet Curated Fashion Finds"
              />
            </div>

            <div className="hidden md:flex flex-col gap-4 w-32">
              <div className="rounded-xl overflow-hidden h-28 relative shadow-lg ring-1 ring-white/10">
                <img
                  src="https://readdy.ai/api/search-image?query=Single%20premium%20white%20sneaker%20displayed%20on%20dark%20charcoal%20stone%20surface%2C%20dramatic%20side%20lighting%20with%20deep%20shadows%2C%20bold%20editorial%20product%20photography%2C%20high%20contrast%20minimalist%20aesthetic%2C%20luxury%20footwear%20catalog%20style&width=300&height=300&seq=hero-thumb1-v2&orientation=squarish"
                  alt="Shoes category"
                  className="w-full h-full object-cover object-top"
                />
                <span className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap">
                  Shoes
                </span>
              </div>
              <div className="rounded-xl overflow-hidden h-28 relative shadow-lg ring-1 ring-white/10">
                <img
                  src="https://readdy.ai/api/search-image?query=Oversized%20slouchy%20hoodie%20in%20oatmeal%20tone%20on%20rustic%20wooden%20table%2C%20soft%20afternoon%20sunlight%20streaming%20through%20linen%20curtains%2C%20cozy%20warm%20atmosphere%2C%20editorial%20streetwear%20photography%2C%20textured%20fabric%20detail%20visible&width=300&height=300&seq=hero-thumb2-v2&orientation=squarish"
                  alt="Hoodies category"
                  className="w-full h-full object-cover object-top"
                />
                <span className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap">
                  Hoodies
                </span>
              </div>
              <div className="rounded-xl overflow-hidden h-28 relative shadow-lg ring-1 ring-white/10">
                <img
                  src="https://readdy.ai/api/search-image?query=Luxury%20structured%20handbag%20in%20cognac%20leather%20on%20white%20marble%20surface%20with%20gold%20accents%2C%20bright%20studio%20lighting%20with%20crisp%20shadows%2C%20premium%20fashion%20product%20photography%2C%20elegant%20minimalist%20aesthetic%2C%20fine%20leather%20grain%20texture&width=300&height=300&seq=hero-thumb3-v2&orientation=squarish"
                  alt="Bags category"
                  className="w-full h-full object-cover object-top"
                />
                <span className="absolute bottom-2 left-2 bg-black/60 text-white text-[10px] font-semibold px-2 py-0.5 rounded-md whitespace-nowrap">
                  Bags
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}